import * as THREE from 'three';

// Layout-specific animations
const applyLayoutSpecificAnimation = (dept, layoutId, progress) => {
  switch (layoutId) {
    case 'quantumCloud':
      // Gentle floating motion
      const floatOffset = Math.sin(Date.now() * 0.001 + dept.name?.length * 0.1) * 2;
      dept.position.y += floatOffset * (1 - progress);
      break;
      
    case 'galacticSpiral':
      // Spiral rotation
      const rotation = progress * Math.PI * 0.5;
      dept.position.applyAxisAngle(new THREE.Vector3(0, 1, 0), rotation * 0.1);
      break;
      
    case 'neuralNetwork':
      // Subtle pulsing
      const pulse = 1 + Math.sin(Date.now() * 0.002) * 0.1;
      dept.scale.setScalar(pulse);
      break;
      
    default:
      // No special animation
      break;
  }
};

// Curve creation functions
const createSpiralCurve = (start, end) => {
  const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  const perpendicular = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
  
  const curveHeight = start.distanceTo(end) * 0.6;
  const controlPoint = midPoint.clone().add(perpendicular.multiplyScalar(curveHeight));
  
  // Add spiral twist
  controlPoint.y += Math.sin(start.angleTo(end)) * 10;
  
  return new THREE.QuadraticBezierCurve3(start, controlPoint, end);
};

const createNeuralCurve = (start, end) => {
  const points = [start];
  const segments = 5;
  const complexity = 0.8;
  
  for (let i = 1; i < segments; i++) {
    const t = i / segments;
    const point = new THREE.Vector3().lerpVectors(start, end, t);
    
    // Add organic jitter
    point.x += (Math.random() - 0.5) * complexity * 15;
    point.y += (Math.random() - 0.5) * complexity * 15;
    point.z += (Math.random() - 0.5) * complexity * 15;
    
    points.push(point);
  }
  
  points.push(end);
  return new THREE.CatmullRomCurve3(points);
};

const createStraightCurve = (start, end) => {
  return new THREE.LineCurve3(start, end);
};

const createOrbitalCurve = (start, end) => {
  const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  const perpendicular = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
  
  const curveHeight = start.distanceTo(end) * 0.5;
  const controlPoint = midPoint.clone().add(perpendicular.multiplyScalar(curveHeight));
  
  // Make it more elliptical
  controlPoint.y += curveHeight * 0.3;
  
  return new THREE.QuadraticBezierCurve3(start, controlPoint, end);
};

const createQuantumCurve = (start, end) => {
  const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  const perpendicular = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
  
  const curveHeight = start.distanceTo(end) * 0.7;
  
  // Quantum randomness
  const randomOffset = new THREE.Vector3(
    (Math.random() - 0.5) * 20,
    (Math.random() - 0.5) * 20,
    (Math.random() - 0.5) * 20
  );
  
  const controlPoint = midPoint.clone()
    .add(perpendicular.multiplyScalar(curveHeight))
    .add(randomOffset);
  
  return new THREE.QuadraticBezierCurve3(start, controlPoint, end);
};

const createDefaultCurve = (start, end) => {
  const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  const perpendicular = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
  
  const curveHeight = start.distanceTo(end) * 0.4;
  const controlPoint = midPoint.clone().add(perpendicular.multiplyScalar(curveHeight));
  
  return new THREE.QuadraticBezierCurve3(start, controlPoint, end);
};

// Recalculate thread curve
const recalculateThreadCurve = (child, departmentsRef, layoutId) => {
  const startDeptId = child.userData?.startDept?.id;
  const endDeptId = child.userData?.endDept?.id;
  
  if (startDeptId && endDeptId) {
    const startDept = departmentsRef.current?.get(startDeptId);
    const endDept = departmentsRef.current?.get(endDeptId);
    
    if (startDept && endDept) {
      // Choose curve based on layout
      const start = new THREE.Vector3(startDept.position.x, startDept.position.y, startDept.position.z);
      const end = new THREE.Vector3(endDept.position.x, endDept.position.y, endDept.position.z);
      
      switch(layoutId) {
        case 'galacticSpiral':
          return createSpiralCurve(start, end);
        case 'neuralNetwork':
          return createNeuralCurve(start, end);
        case 'crystalLattice':
          return createStraightCurve(start, end);
        case 'solarSystem':
          return createOrbitalCurve(start, end);
        case 'quantumCloud':
          return createQuantumCurve(start, end);
        default:
          return createDefaultCurve(start, end);
      }
    }
  }
  return null;
};

// Animation utilities
export const createLayoutAnimation = (
  departmentsRef, 
  newPositions, 
  layoutId, 
  scene,
  duration = 1500
) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const originalPositions = new Map();
    
    // Save original positions
    departmentsRef.current?.forEach((dept, id) => {
      if (dept?.position) {
        originalPositions.set(id, dept.position.clone());
      }
    });
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      
      departmentsRef.current?.forEach((dept, id) => {
        const original = originalPositions.get(id);
        const target = newPositions.get(id);
        
        if (dept?.position && original && target) {
          dept.position.lerpVectors(original, target, easeProgress);
          
          // Layout-specific animations
          applyLayoutSpecificAnimation(dept, layoutId, easeProgress);
          
          // Scale effect
          const scale = 1 + Math.sin(progress * Math.PI) * 0.3;
          dept.scale.setScalar(scale);
        }
      });
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    };
    
    animate();
  });
};
/*
// Curve optimization
export const createLayoutOptimizedCurve = (start, end, layoutId) => {
  const startVec = new THREE.Vector3(start[0], start[1], start[2]);
  const endVec = new THREE.Vector3(end[0], end[1], end[2]);
  
  switch(layoutId) {
    case 'galacticSpiral':
      return createSpiralCurve(startVec, endVec);
    case 'neuralNetwork':
      return createNeuralCurve(startVec, endVec);
    case 'crystalLattice':
      return createStraightCurve(startVec, endVec);
    case 'solarSystem':
      return createOrbitalCurve(startVec, endVec);
    case 'quantumCloud':
      return createQuantumCurve(startVec, endVec);
    default:
      return createDefaultCurve(startVec, endVec);
  }
};
*/
// Update thread curves
export const updateThreadCurves = async (threadsRef, departmentsRef, layoutId) => {
  return new Promise((resolve) => {
    if (!threadsRef.current) {
      resolve();
      return;
    }
    
    threadsRef.current.forEach((threadObj) => {
      if (threadObj?.group) {
        threadObj.group.children.forEach(child => {
          if (child?.userData?.type === 'thread-connection') {
            // Recalculate curve based on new positions
            const newCurve = recalculateThreadCurve(child, departmentsRef, layoutId);
            if (newCurve && child.geometry) {
              child.userData.curve = newCurve;
              const points = newCurve.getPoints(50);
              child.geometry.setFromPoints(points);
            }
          }
        });
      }
    });
    setTimeout(resolve, 100);
  });
};

// Calculate optimal curves
export const calculateOptimalCurves = (departmentsRef, layoutId) => {
  const curves = new Map();
  
  // Implementation would calculate optimal curves for the layout
  // For now, return empty map
  return curves;
};

// Helper function to update particle paths
export const updateParticlePathsForThread = (threadObj, curve) => {
  if (!threadObj?.group || !curve) return;
  
  threadObj.group.children.forEach(child => {
    if (child?.userData?.type === 'data-particle') {
      child.userData.curve = curve;
      // Reset particle progress
      child.userData.progress = Math.random();
      
      // Position particle on curve
      const position = curve.getPointAt(child.userData.progress);
      child.position.copy(position);
    }
  });
};

//Mandar
// Utility functions for managing thread visibility in 3D

/**
 * Initialize thread objects in 3D scene
 */
export const initializeThreadsInScene = (scene, threadsData) => {
  const threadObjects = new Map();
  
  threadsData.forEach(thread => {
    // Create thread group
    const threadGroup = new THREE.Group();
    threadGroup.name = `thread-${thread.id}`;
    threadGroup.userData = { type: 'thread', threadId: thread.id };
    threadGroup.visible = true; // Initially visible
    
    // Create thread connection lines
    const geometry = new THREE.BufferGeometry();
    const material = new THREE.LineBasicMaterial({ 
      color: new THREE.Color(thread.color || '#45b7d1'),
      linewidth: 2 
    });
    
    const line = new THREE.Line(geometry, material);
    line.userData = { type: 'thread-connection', threadId: thread.id };
    threadGroup.add(line);
    
    // Create thread label
    const label = createThreadLabel(thread.name, thread.color);
    label.userData = { type: 'thread-label', threadId: thread.id };
    threadGroup.add(label);
    
    // Add to scene
    scene.add(threadGroup);
    
    // Store reference
    threadObjects.set(thread.id, {
      group: threadGroup,
      label: label,
      line: line
    });
  });
  
  return threadObjects;
};

/**
 * Update thread visibility in 3D scene
 */
export const updateThreadVisibility = (threadObjects, visibleThreadIds) => {
  threadObjects.forEach((threadObject, threadId) => {
    const isVisible = visibleThreadIds.includes(threadId);
    
    // Set visibility of entire thread group
    if (threadObject.group) {
      threadObject.group.visible = isVisible;
      
      // Also traverse and update all children
      threadObject.group.traverse((child) => {
        child.visible = isVisible;
      });
    }
    
    // Special handling for thread glow/effects
    if (threadObject.glow) {
      threadObject.glow.visible = isVisible;
    }
  });
};

/**
 * Highlight selected threads in 3D
 */
export const highlightSelectedThreads = (threadObjects, selectedThreadIds) => {
  threadObjects.forEach((threadObject, threadId) => {
    const isSelected = selectedThreadIds.includes(threadId);
    
    if (threadObject.line && threadObject.line.material) {
      // Change line color for selected threads
      if (isSelected) {
        threadObject.line.material.color.setHex(0x00ff00); // Green for selected
        threadObject.line.material.linewidth = 4;
        threadObject.line.material.opacity = 0.9;
      } else {
        threadObject.line.material.color.setHex(threadObject.originalColor || 0x45b7d1);
        threadObject.line.material.linewidth = 2;
        threadObject.line.material.opacity = 0.6;
      }
    }
    
    // Add glow effect for selected threads
    if (isSelected && !threadObject.glow) {
      threadObject.glow = createThreadGlow(threadId);
      threadObject.group.add(threadObject.glow);
    } else if (!isSelected && threadObject.glow) {
      threadObject.group.remove(threadObject.glow);
      threadObject.glow = null;
    }
  });
};

/**
 * Create thread label in 3D
 */
const createThreadLabel = (text, color) => {
  // Implementation for 3D text label
  // This would create a sprite or 3D text mesh
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = 256;
  canvas.height = 128;
  
  context.fillStyle = color || '#45b7d1';
  context.font = 'bold 24px Arial';
  context.textAlign = 'center';
  context.fillText(text, 128, 40);
  
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ map: texture });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(10, 5, 1);
  
  return sprite;
};

/**
 * Create glow effect for selected threads
 */
const createThreadGlow = (threadId) => {
  const geometry = new THREE.SphereGeometry(1.5, 16, 16);
  const material = new THREE.MeshBasicMaterial({
    color: 0x00ff00,
    transparent: true,
    opacity: 0.3,
    side: THREE.BackSide
  });
  
  const glow = new THREE.Mesh(geometry, material);
  glow.userData = { type: 'thread-glow', threadId: threadId };
  
  return glow;
};

/**
 * Get thread statistics for UI
 */
export const getThreadStatistics = (threadsData, visibleThreadIds) => {
  const visibleThreads = threadsData.filter(t => visibleThreadIds.includes(t.id));
  const hiddenThreads = threadsData.filter(t => !visibleThreadIds.includes(t.id));
  
  const totalConnections = visibleThreads.reduce((sum, t) => {
    return sum + (t.connects?.length || 0);
  }, 0);
  
  const avgHealthScore = visibleThreads.length > 0
    ? Math.round(visibleThreads.reduce((sum, t) => sum + (t.healthScore || 0), 0) / visibleThreads.length)
    : 0;
  
  return {
    visible: visibleThreads.length,
    hidden: hiddenThreads.length,
    total: threadsData.length,
    totalConnections,
    avgHealthScore,
    criticalVisible: visibleThreads.filter(t => t.critical).length,
    goldenVisible: visibleThreads.filter(t => t.isGolden).length
  };
};

// NEW: Calculate optimal layout based on selected threads
export const calculateOptimalLayout = (departments, selectedThreads) => {
  if (selectedThreads.length === 0) return 'galacticSpiral';
  
  // Analyze thread characteristics
  const threadCount = selectedThreads.length;
  const avgConnections = selectedThreads.reduce((sum, t) => {
    const connects = (t.connects || []).length;
    const domains = (t.domains || []).length;
    const systems = (t.systems || []).length;
    return sum + connects + domains + systems;
  }, 0) / threadCount;
  
  const criticalCount = selectedThreads.filter(t => t.critical).length;
  const goldenCount = selectedThreads.filter(t => t.isGolden).length;
  
  // Choose layout based on characteristics
  if (criticalCount > 0 && threadCount <= 3) {
    return 'neuralNetwork'; // Best for critical threads
  } else if (avgConnections > 5) {
    return 'galacticSpiral'; // Good for many connections
  } else if (goldenCount > 0) {
    return 'solarSystem'; // Highlights important threads
  } else if (threadCount >= 4) {
    return 'crystalLattice'; // Good for multiple threads
  } else {
    return 'quantumCloud'; // Default for mixed threads
  }
};

// Enhanced curve creation with distance optimization
export const createLayoutOptimizedCurve = (start, end, layoutId, threadData, isSelected = false) => {
  const startVec = new THREE.Vector3(start[0], start[1], start[2]);
  const endVec = new THREE.Vector3(end[0], end[1], end[2]);
  
  const distance = startVec.distanceTo(endVec);
  
  // Optimize curve based on distance and layout
  switch(layoutId) {
    case 'galacticSpiral':
      return createSpiralCurve(startVec, endVec, distance, isSelected);
    case 'neuralNetwork':
      return createNeuralCurve(startVec, endVec, distance, isSelected);
    case 'crystalLattice':
      return createStraightCurve(startVec, endVec, distance, isSelected);
    case 'solarSystem':
      return createOrbitalCurve(startVec, endVec, distance, isSelected);
    case 'quantumCloud':
      return createQuantumCurve(startVec, endVec, distance, isSelected);
    case 'optimized':
      return createOptimizedCurve(startVec, endVec, distance, threadData, isSelected);
    default:
      return createDefaultCurve(startVec, endVec, distance, isSelected);
  }
};

// Create optimized curve for shortest path
export const createOptimizedCurve = (start, end, distance, threadData, isSelected) => {
  // For optimized layout, create minimal curve with slight arc
  const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  
  // Minimal arc for readability
  const arcHeight = Math.min(distance * 0.2, 10);
  const perpendicular = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
  
  const controlPoint = midPoint.clone().add(perpendicular.multiplyScalar(arcHeight));
  
  // Add slight elevation for selected threads
  if (isSelected) {
    controlPoint.y += 5;
  }
  
  return new THREE.QuadraticBezierCurve3(start, controlPoint, end);
};

// Enhanced thread curve updater
export const updateThreadCurvesForLayout = (threadsRef, departmentsRef, layoutId, selectedThreadIds) => {
  return new Promise((resolve) => {
    if (!threadsRef.current) {
      resolve();
      return;
    }
    
    let updates = 0;
    const total = threadsRef.current.size;
    
    threadsRef.current.forEach((threadObj, threadId) => {
      if (threadObj?.group) {
        const isSelected = selectedThreadIds.includes(threadId);
        
        threadObj.group.children.forEach(child => {
          if (child?.userData?.type === 'thread-connection') {
            // Recalculate curve
            const newCurve = recalculateThreadCurve(child, departmentsRef, layoutId, isSelected);
            if (newCurve && child.geometry) {
              child.userData.curve = newCurve;
              const points = newCurve.getPoints(50);
              child.geometry.setFromPoints(points);
              
              // Update material for selected threads
              if (isSelected) {
                child.material.color.setHex(0x00ff00);
                child.material.linewidth = 4;
                child.material.opacity = 0.9;
              }
            }
          }
        });
        
        updates++;
      }
    });
    
    // Wait for all updates
    if (updates === total) {
      resolve();
    } else {
      setTimeout(() => resolve(), 100);
    }
  });
};

// Post-processing tickmark system
export const createTickmarkSystem = (selectedThreadIds, scene, departmentsRef) => {
  const tickmarkGroup = new THREE.Group();
  tickmarkGroup.name = 'tickmarks';
  
  selectedThreadIds.forEach(threadId => {
    // Create green circle tickmark
    const geometry = new THREE.RingGeometry(0.8, 1, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    
    const tickmark = new THREE.Mesh(geometry, material);
    tickmark.userData = { threadId: threadId, type: 'tickmark' };
    
    // Position near thread (you'll need to calculate position)
    // This is a placeholder - you'll need to integrate with your thread positioning
    tickmark.position.set(
      Math.random() * 50 - 25,
      30,
      Math.random() * 50 - 25
    );
    
    tickmarkGroup.add(tickmark);
  });
  
  scene.add(tickmarkGroup);
  return tickmarkGroup;
};

// Calculate thread statistics for UI
export const calculateThreadStats = (threads, selectedThreadIds) => {
  const selected = threads.filter(t => selectedThreadIds.includes(t.id));
  const totalConnections = selected.reduce((sum, t) => {
    return sum + (t.connects?.length || 0) + (t.domains?.length || 0) + (t.systems?.length || 0);
  }, 0);
  
  const avgDistance = 85; // Placeholder - calculate actual average distance
  
  return {
    selectedCount: selected.length,
    totalConnections,
    avgDistance,
    optimizationScore: Math.min(100, Math.round(avgDistance * 1.2))
  };
};

//Mandar End
// In utils.js, add these functions:

/**
 * Create and position sequence numbers for a thread
 */
export const createThreadSequenceNumbers = (threadId, threadData, position) => {
  const sequenceGroup = new THREE.Group();
  sequenceGroup.name = `thread-${threadId}-sequence`;
  
  // Create sequence numbers based on thread steps/connections
  const steps = threadData.sequence || threadData.steps || [];
  steps.forEach((step, index) => {
    const seqNumber = createSequenceNumber(index + 1, step);
    seqNumber.userData = {
      threadId: threadId,
      type: 'sequence-number',
      sequenceIndex: index,
      originalOffset: { x: index * 4, y: 0, z: 0 }
    };
    
    // Position relative to thread
    seqNumber.position.set(
      position[0] + (index * 4),
      position[1] + 5,
      position[2]
    );
    
    sequenceGroup.add(seqNumber);
  });
  
  return sequenceGroup;
};

/**
 * Create a sequence number element
 */
const createSequenceNumber = (number, stepData) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = 64;
  canvas.height = 64;
  
  // Draw number circle
  context.beginPath();
  context.arc(32, 32, 28, 0, Math.PI * 2);
  context.fillStyle = 'rgba(69, 183, 209, 0.8)';
  context.fill();
  context.strokeStyle = '#ffffff';
  context.lineWidth = 3;
  context.stroke();
  
  // Draw number
  context.fillStyle = '#ffffff';
  context.font = 'bold 24px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(number.toString(), 32, 32);
  
  // Add step name if provided
  if (stepData?.name) {
    context.font = '10px Arial';
    context.fillText(stepData.name.substring(0, 8), 32, 52);
  }
  
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ 
    map: texture,
    transparent: true 
  });
  
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(3, 3, 1);
  
  return sprite;
};

/**
 * Update all sequence numbers in scene after transformation
 */
export const updateAllSequenceNumbers = (threadsRef, threadPositions, layoutId) => {
  if (!threadsRef.current) return;
  
  threadsRef.current.forEach((threadObj, threadId) => {
    const position = threadPositions.get(threadId);
    if (position && threadObj.group) {
      // Update sequence numbers in thread group
      threadObj.group.children.forEach(child => {
        if (child.userData?.type === 'sequence-number') {
          const offset = child.userData.originalOffset || { x: 0, y: 0, z: 0 };
          const newPos = calculateLayoutPosition(position, offset, layoutId, child.userData.sequenceIndex);
          child.position.set(...newPos);
        }
      });
    }
  });
};

/**
 * Calculate position based on layout
 */
const calculateLayoutPosition = (basePos, offset, layoutId, sequenceIndex) => {
  switch(layoutId) {
    case 'galacticSpiral':
      const angle = sequenceIndex * Math.PI / 3;
      return [
        basePos[0] + Math.cos(angle) * 8,
        basePos[1] + offset.y + 4,
        basePos[2] + Math.sin(angle) * 8
      ];
    case 'neuralNetwork':
      return [
        basePos[0] + offset.x * 0.8,
        basePos[1] + offset.y + 3 + (sequenceIndex % 2) * 2,
        basePos[2] + offset.z * 0.8
      ];
    case 'crystalLattice':
      return [
        basePos[0] + offset.x,
        basePos[1] + offset.y + sequenceIndex * 2,
        basePos[2] + offset.z
      ];
    default:
      return [
        basePos[0] + offset.x,
        basePos[1] + offset.y + 5,
        basePos[2] + offset.z
      ];
  }
};

// Export all curve functions for testing
export {
  createSpiralCurve,
  createNeuralCurve,
  createStraightCurve,
  createOrbitalCurve,
  createQuantumCurve,
  createDefaultCurve,
  applyLayoutSpecificAnimation,
  recalculateThreadCurve
};